import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useReducedMotion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Check,
  Search,
  FileText,
  TrendingUp,
  ShieldCheck,
  BarChart3,
  Sparkles,
  X,
  ChevronDown,
  Linkedin,
} from "lucide-react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { mountCalInline } from "@/lib/cal-bootstrap";
import { LINKEDIN_URL } from "@/lib/constants";
import { useTranslation, useLang } from "@/lib/i18n";
import { usePostHog } from "@posthog/react";
import { AvailabilityPill } from "@/components/AvailabilityPill";
import AIStackPills from "@/components/AIStackPills";
import FooterCTA from "@/components/FooterCTA";
import Navbar from "@/components/Navbar";
import ContactForm from "@/components/ContactForm";

const triptychIcons = [Search, FileText, TrendingUp];
const triptychColors = ["text-primary", "text-amber-500", "text-primary"] as const;
const triptychBorderColors = [
  "border-primary/20",
  "border-amber-500/20",
  "border-primary/20",
] as const;
const triptychLabelColors = [
  "text-primary",
  "text-amber-500",
  "text-primary",
] as const;

const credibilityIcons = [ShieldCheck, BarChart3, Sparkles];

/* ── renderWords helper ─────────────────────────────────────────────── */
function renderWords(
  text: string,
  baseDelay: number,
  className: string,
  prefersReduced: boolean | null,
  isMobile: boolean = false,
) {
  const staticRender = prefersReduced || isMobile;
  return text.split(" ").map((word, i) => (
    <motion.span
      key={i}
      initial={staticRender ? { opacity: 1 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        staticRender
          ? { duration: 0.2 }
          : { delay: baseDelay + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }
      }
      className={`inline-block mr-[0.1em] pr-[0.08em] ${className}`}
    >
      {word}
    </motion.span>
  ));
}

/* ── StepCountUp (00 → target) ──────────────────────────────────────── */
function StepCountUp({ target }: { target: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState("00");

  useEffect(() => {
    if (!inView) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) { setDisplay(String(target).padStart(2, "0")); return; }
    const start = performance.now();
    const dur = 800;
    const step = (now: number) => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(String(Math.round(target * eased)).padStart(2, "0"));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target]);

  return <span ref={ref}>{display}</span>;
}

const Advisory = () => {
  const prefersReduced = useReducedMotion();
  const t = useTranslation();
  const { lang } = useLang();
  const a = t.oneToOne;
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const anim = {
    initial: prefersReduced || isMobile ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  const canonical = lang === "pl" ? "https://chrisworkflows.com/pl/1-1" : "https://chrisworkflows.com/1-1";
  useDocumentMeta({
    title: a.meta.title,
    description: a.meta.description,
    canonical,
    robots: "index, follow",
    ogTitle: a.meta.title,
    ogDescription: a.meta.description,
    ogImage: `https://chrisworkflows.com/og/advisory-${lang}.jpg?v=3`,
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "1:1 Consulting",
      "serviceType": "Strategic Advisory",
      "description": a.meta.description,
      "url": canonical,
      "provider": { "@id": "https://chrisworkflows.com/#chris" },
      "areaServed": { "@type": "Place", "name": "Worldwide" },
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Founders, CEOs, senior marketing leaders",
      },
      "offers": {
        "@type": "Offer",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": "500",
          "priceCurrency": "PLN",
          "description": "50-minute session",
        },
      },
    },
  });

  const posthog = usePostHog();

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      const origin = typeof e.origin === "string" ? e.origin : "";
      if (!origin.endsWith("cal.com")) return;
      const data = e.data;
      if (typeof data !== "object" || !data) return;
      const action = (data as { type?: string; action?: string }).action
        ?? (data as { type?: string }).type;
      if (typeof action === "string" && /booking.*success/i.test(action)) {
        posthog?.capture("discovery_call_booked", { page: "advisory", action });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [posthog]);

  useEffect(() => {
    const target = document.getElementById("cal-advisory");
    if (!target) return;
    let cleanup: (() => void) | null = null;
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      cleanup = mountCalInline({
        elementOrSelector: "#cal-advisory",
        calLink: "chris-scibiorek/30min",
      });
    };
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          start();
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(target);
    const safety = setTimeout(start, window.innerWidth < 1024 ? 6000 : 3000);
    return () => {
      observer.disconnect();
      clearTimeout(safety);
      cleanup?.();
    };
  }, []);

  /* ── Hero spotlight + magnetic CTA ───────────────────────────────── */
  const heroRef = useRef<HTMLDivElement>(null);
  const spotlightRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  const ctaContainerRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const scrollOpacity = useTransform(scrollY, [0, 60], [1, 0]);

  const isFinePointer = typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;

  const handleHeroMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (prefersReduced || !isFinePointer) return;
      const rect = heroRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (spotlightRef.current) {
        spotlightRef.current.style.background = `radial-gradient(600px circle at ${x}px ${y}px, hsl(166 84% 46% / 0.06), transparent 60%)`;
      }
      if (ctaRef.current && ctaContainerRef.current) {
        const cr = ctaContainerRef.current.getBoundingClientRect();
        const cx = cr.left + cr.width / 2;
        const cy = cr.top + cr.height / 2;
        const dx = Math.max(-8, Math.min(8, (e.clientX - cx) * 0.08));
        const dy = Math.max(-8, Math.min(8, (e.clientY - cy) * 0.08));
        ctaRef.current.style.transform = `translate(${dx}px, ${dy}px)`;
      }
    },
    [prefersReduced, isFinePointer],
  );
  const handleHeroMouseLeave = useCallback(() => {
    if (spotlightRef.current) spotlightRef.current.style.background = "none";
    if (ctaRef.current) ctaRef.current.style.transform = "translate(0,0)";
  }, []);

  /* ── S5 neighbor-dim state ─────────────────────────────────────────── */
  const [hoveredPill, setHoveredPill] = useState<number | null>(null);

  /* ── S6 timeline scroll-linked line ────────────────────────────────── */
  const timelineRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: timelineProgress } = useScroll({
    target: timelineRef,
    offset: ["start 70%", "end 30%"],
  });
  const pathLength = useTransform(timelineProgress, [0, 1], [0, 1]);

  /* ── S3 triptych card glow ─────────────────────────────────────────── */
  const handleCardMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isFinePointer) return;
      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--card-mx", `${e.clientX - rect.left}px`);
      el.style.setProperty("--card-my", `${e.clientY - rect.top}px`);
    },
    [isFinePointer],
  );

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* ── S1: Hero ─────────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        onMouseMove={handleHeroMouseMove}
        onMouseLeave={handleHeroMouseLeave}
        className="relative flex flex-col min-h-[85vh] pt-[72px] sm:pt-10"
      >
        {/* Spotlight overlay */}
        <div ref={spotlightRef} className="absolute inset-0 pointer-events-none z-[1]" />

        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(hsl(166 84% 46%) 1px, transparent 1px), linear-gradient(90deg, hsl(166 84% 46%) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="absolute top-1/3 right-1/4 w-[400px] h-[400px] rounded-full bg-primary/5 blur-[100px]" />

        <div className="flex-1 flex items-center">
          <div className="container mx-auto px-4 sm:px-6 xl:max-w-[1400px] relative z-10">
            <div className="flex items-center gap-16">
              {/* Left: text */}
              <div className="max-w-3xl flex-1">
                <motion.p
                  initial={anim.initial}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, ...anim.transition }}
                  className="flex items-center gap-3 text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-8"
                >
                  <span className="w-8 h-px bg-primary animate-pulse-glow" />
                  {a.hero.eyebrow}
                </motion.p>

                <h1 className="text-5xl sm:text-7xl lg:text-8xl font-display leading-[1.1] tracking-tight mb-8">
                  {renderWords(a.hero.headingBefore, 0.3, "", prefersReduced, isMobile)}
                  <br className="hidden sm:block" />
                  {renderWords(a.hero.headingHighlight, 0.3 + a.hero.headingBefore.split(" ").length * 0.06, "text-gradient", prefersReduced, isMobile)}
                </h1>

                <motion.p
                  initial={anim.initial}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, ...anim.transition }}
                  className="text-lg font-body text-muted-foreground leading-relaxed max-w-xl mb-6"
                >
                  {a.hero.sub}
                </motion.p>

                <motion.p
                  initial={anim.initial}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, ...anim.transition }}
                  className="text-sm font-body text-soft-foreground mb-10"
                >
                  {a.hero.meta}
                </motion.p>

                <motion.div
                  initial={anim.initial}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, ...anim.transition }}
                  className="flex flex-wrap items-center gap-4"
                >
                  <div ref={ctaContainerRef}>
                    <a
                      ref={ctaRef}
                      href="#book"
                      className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-lg bg-amber-500 text-black font-body font-semibold text-sm hover:bg-amber-400 transition-all"
                      style={{ willChange: "transform" }}
                    >
                      {a.hero.ctaPrimary}
                      <ArrowRight
                        size={16}
                        className="group-hover:translate-x-0.5 transition-transform"
                      />
                    </a>
                  </div>
                  <a
                    href="#how-it-works"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-body font-medium text-foreground border border-border hover:border-primary/40 transition-colors"
                  >
                    {a.hero.ctaSecondary}
                  </a>
                </motion.div>
              </div>

              {/* Right: circular photo with badges */}
              <div className="hidden lg:flex flex-shrink-0">
                <div className="relative w-64 h-64" style={{ overflow: "visible" }}>
                  {/* Teal glow — breathing */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      boxShadow: "0 0 40px rgba(19, 216, 170, 0.2)",
                      ...(prefersReduced ? {} : { animation: "glow-breathe 6s ease-in-out infinite" }),
                    }}
                  />
                  <div className="absolute inset-0 rounded-full border-2 border-primary/30" />

                  {/* Dashed SVG ring */}
                  {!prefersReduced && (
                    <svg
                      className="absolute inset-[-12px] w-[calc(100%+24px)] h-[calc(100%+24px)] pointer-events-none z-[5]"
                      viewBox="0 0 288 288"
                      style={{ animation: "spin 30s linear infinite" }}
                    >
                      <circle
                        cx="144"
                        cy="144"
                        r="140"
                        fill="none"
                        stroke="hsl(166 84% 46%)"
                        strokeWidth="1"
                        strokeDasharray="4 4"
                        strokeOpacity="0.4"
                      />
                    </svg>
                  )}

                  <picture>
                    <source srcSet="/images/chris.webp" type="image/webp" />
                    <motion.img
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4, duration: 0.6 }}
                      src="/images/chris.jpg"
                      alt="Chris Scibiorek"
                      width={615}
                      height={800}
                      className="w-full h-full rounded-full object-cover object-top relative z-10 border border-accent/20"
                      style={{ boxShadow: "0 0 40px -10px rgba(245, 158, 11, 0.2)" }}
                    />
                  </picture>

                  {/* Stats badge: top-left — staggered */}
                  <motion.div
                    initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: -10, x: -4 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    transition={{ delay: 0.8, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute -top-4 -left-8 z-20 rounded-xl border border-primary/30 bg-[#0d1520] px-3 py-2"
                  >
                    <p className="text-lg font-display text-primary leading-none">
                      {a.hero.photoStatsBadge.value}
                    </p>
                    <p className="text-[10px] font-mono font-semibold tracking-widest text-primary/80 uppercase mt-1">
                      {a.hero.photoStatsBadge.label}
                    </p>
                  </motion.div>

                  {/* Availability pill: top-right — staggered */}
                  <motion.div
                    initial={prefersReduced ? { opacity: 0 } : { opacity: 0, y: -10, x: 4 }}
                    animate={{ opacity: 1, y: 0, x: 0 }}
                    transition={{ delay: 0.9, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute -top-4 -right-2 xl:-right-12 z-20"
                  >
                    <AvailabilityPill
                      text={a.hero.availability}
                      accent="amber"
                    />
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll affordance */}
        <motion.div
          style={{ opacity: scrollOpacity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-1 z-10"
        >
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{t.scrollLabel}</span>
          <ChevronDown size={14} className="text-muted-foreground animate-bounce" />
        </motion.div>
      </section>

      {/* ── Supporting line ───────────────────────────────────────────────────── */}
      <section className="py-12 border-b border-border/40">
        <div className="container mx-auto px-6">
          <motion.p
            initial={prefersReduced ? undefined : { opacity: 0, y: 16 }}
            whileInView={prefersReduced ? undefined : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="text-3xl sm:text-4xl lg:text-5xl font-display leading-tight tracking-tight text-center max-w-3xl mx-auto"
          >
            {a.supportingLineParts.map((part, i) =>
              part.highlight ? (
                <span key={i} className="text-gradient">{part.text}</span>
              ) : (
                <span key={i}>{part.text}</span>
              )
            )}
          </motion.p>
        </div>
      </section>

      {/* ── S2: Triptych ──────────────────────────────────────────────────────── */}
      <section className="py-12 bg-card/20 border-b border-border/40">
        <div className="container mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {a.triptych.map((item, i) => {
              const Icon = triptychIcons[i];
              const isAmber = i === 1;
              const glowColor = isAmber ? "rgba(245,158,11,0.06)" : "hsl(166 84% 46% / 0.06)";
              return (
                <motion.div
                  key={i}
                  initial={anim.initial}
                  whileInView={anim.whileInView}
                  viewport={anim.viewport}
                  transition={{ delay: i * 0.1, ...anim.transition }}
                  // Amber middle card breathing border via framer-motion
                  {...(isAmber && !prefersReduced
                    ? {
                        animate: {
                          borderColor: [
                            "rgba(245,158,11,0.2)",
                            "rgba(245,158,11,0.4)",
                            "rgba(245,158,11,0.2)",
                          ],
                        },
                        transition: {
                          delay: i * 0.1,
                          ...anim.transition,
                          borderColor: { duration: 5, ease: "easeInOut", repeat: Infinity },
                        },
                      }
                    : {})}
                  onMouseMove={handleCardMouseMove}
                  onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.currentTarget.style.removeProperty("--card-mx");
                    e.currentTarget.style.removeProperty("--card-my");
                  }}
                  className={`relative rounded-2xl border ${triptychBorderColors[i]} bg-[#0d1520] p-6 hover:border-opacity-40 [@media(pointer:fine)]:hover:-translate-y-0.5 transition-[border-color,transform,box-shadow] flex flex-col`}
                  style={{
                    background: isFinePointer
                      ? `radial-gradient(600px circle at var(--card-mx, 50%) var(--card-my, 50%), ${glowColor}, transparent 60%), #0d1520`
                      : undefined,
                  }}
                >
                  {/* Icon top-left */}
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 ${triptychColors[i]} mb-5`}
                  >
                    <Icon size={20} />
                  </div>
                  <h2 className="text-lg font-display text-foreground mb-2">
                    {item.label}
                  </h2>
                  <p className="text-sm font-body text-muted-foreground leading-relaxed flex-1">
                    {item.desc}
                  </p>
                  {/* Bottom label */}
                  <p
                    className={`text-[10px] font-mono font-semibold tracking-widest uppercase mt-5 ${triptychLabelColors[i]}`}
                  >
                    {item.bottomLabel}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── S3: Who this is for ───────────────────────────────────────────────── */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="mb-14"
          >
            <h2 className="flex items-center gap-3 text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              <span className="w-8 h-px bg-primary" />
              {a.whoFor.eyebrow}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-6">
            {a.whoFor.items.map((item, i) => (
              <motion.div
                key={i}
                initial={anim.initial}
                whileInView={anim.whileInView}
                viewport={anim.viewport}
                transition={{
                  delay: i * 0.08,
                  ...anim.transition,
                }}
                className="relative group"
              >
                <span className="text-5xl sm:text-6xl font-display leading-none text-primary/25 select-none block mb-2 group-hover:text-primary/60 group-hover:translate-x-1 transition-all duration-200">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="text-lg sm:text-xl font-display text-foreground leading-snug mb-2">
                  {item.title}
                </h3>
                <p className="text-sm font-body text-muted-foreground leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── S4: Strategic Focus Area (pill row) ──────────────────────────────── */}
      <section className="py-12 bg-card/40">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="mb-10"
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {a.workOn.eyebrow}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
              {a.workOn.heading}
            </h2>
          </motion.div>

          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.15, ...anim.transition }}
            className="flex flex-wrap gap-3 max-w-3xl"
          >
            {a.workOn.items.map((item, i) => (
              <span
                key={item}
                onMouseEnter={() => setHoveredPill(i)}
                onMouseLeave={() => setHoveredPill(null)}
                className={`border rounded-full px-4 py-2 text-primary text-sm font-body transition-all duration-150 cursor-default ${
                  hoveredPill === null || hoveredPill === i
                    ? "border-primary/60 opacity-100"
                    : "border-primary/30 opacity-40"
                } ${hoveredPill === i ? "border-primary" : ""}`}
              >
                {item}
              </span>
            ))}
          </motion.div>

          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.3, ...anim.transition }}
            className="mt-8"
          >
            <span className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-primary/30 bg-primary/5">
              <span
                className="text-primary text-[10px] inline-block"
                style={prefersReduced ? {} : { animation: "spin 8s linear infinite" }}
              >
                ◆
              </span>
              <p className="text-sm font-body font-medium text-foreground">
                {a.workOn.closer}
              </p>
            </span>
          </motion.div>
        </div>
      </section>

      {/* ── S5: The Session (horizontal timeline) ────────────────────────────── */}
      <section id="how-it-works" className="py-16" ref={timelineRef}>
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="mb-16"
          >
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
              {a.howItWorks.heading}
            </h2>
          </motion.div>

          <div className="relative max-w-5xl">
            {/* Horizontal SVG line on lg */}
            <svg
              className="hidden lg:block absolute top-7 left-[2.5%] right-[15%] w-[82.5%] h-[2px] overflow-visible"
              preserveAspectRatio="none"
            >
              <motion.line
                x1="0"
                y1="1"
                x2="100%"
                y2="1"
                stroke="hsl(166 84% 46%)"
                strokeWidth="1"
                strokeOpacity="0.3"
                style={{ pathLength }}
              />
            </svg>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-6">
              {a.howItWorks.steps.map((step, i) => (
                <motion.div
                  key={i}
                  initial={anim.initial}
                  whileInView={anim.whileInView}
                  viewport={anim.viewport}
                  transition={{ delay: i * 0.12, ...anim.transition }}
                  className="flex flex-col items-center sm:items-start text-center sm:text-left"
                >
                  <motion.div
                    className={`relative w-14 h-14 rounded-full border-2 flex items-center justify-center mb-6 flex-shrink-0 z-10 ${
                      i === 0
                        ? "border-primary bg-primary/20"
                        : "border-primary/40 bg-primary/10"
                    }`}
                    {...(!prefersReduced
                      ? {
                          whileInView: {
                            boxShadow: [
                              "0 0 0 4px hsl(166 84% 46% / 0.4)",
                              "0 0 0 0px transparent",
                            ],
                          },
                          transition: { delay: i * 0.12 + 0.3, duration: 1.8, ease: "easeOut" },
                          viewport: { once: true },
                        }
                      : {})}
                  >
                    <span className="font-display text-lg text-primary">
                      <StepCountUp target={i + 1} />
                    </span>
                  </motion.div>
                  <p className="text-xs font-mono font-semibold tracking-widest text-primary uppercase mb-3">
                    {step.label}
                  </p>
                  <p className="text-sm font-body text-muted-foreground leading-relaxed">
                    {step.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── S6: Expectations ─────────────────────────────────────────────────── */}
      <section className="py-16 bg-card/40">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="mb-12"
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {a.expectations.eyebrow}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
              {a.expectations.heading}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {a.expectations.items.map((item, i) => (
              <ExpectationCard key={i} index={i} item={item} anim={anim} prefersReduced={prefersReduced} />
            ))}
          </div>
        </div>
      </section>

      {/* ── S7: AI in the room (tools) ───────────────────────────────────────── */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="mb-12"
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {a.tools.eyebrow}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-6">
              {a.tools.heading}
            </h2>
            <p className="text-sm font-body text-muted-foreground max-w-2xl leading-relaxed">
              {a.tools.desc}
            </p>
          </motion.div>

          <AIStackPills items={t.aiTeams.stack.items} />
        </div>
      </section>

      {/* ── S8: Why work with me ──────────────────────────────────────────────── */}
      <section className="py-16 bg-card/40">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-5xl">
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={anim.transition}
            >
              <p className="flex items-center gap-3 text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
                <span className="w-8 h-px bg-primary" />
                {a.whyMe.eyebrow}
              </p>
              <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-8">
                {a.whyMe.heading}
              </h2>
              <div className="space-y-5 mb-8">
                {a.whyMe.credibility.map((item, i) => {
                  const Icon = credibilityIcons[i];
                  return (
                    <div key={i} className="flex items-start gap-4 border-l-[3px] border-primary/40 pl-5">
                      <div className="flex items-center gap-2 flex-shrink-0 mt-0.5">
                        <Icon size={18} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-body font-semibold text-foreground">
                          {item.title}
                        </p>
                        <p className="text-sm font-body text-muted-foreground leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="relative mt-8 p-5 rounded-xl border border-primary/20 bg-primary/5">
                <span className="absolute top-4 left-4 font-display text-6xl text-primary/20 pointer-events-none select-none leading-none">"</span>
                <p className="text-base font-display text-foreground pl-8">
                  {a.whyMe.closer}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.2, ...anim.transition }}
              className="flex flex-col items-center lg:items-end"
            >
              <div className="relative w-56 h-56" style={{ overflow: "visible" }}>
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    boxShadow: "0 0 40px rgba(19, 216, 170, 0.2)",
                    ...(prefersReduced ? {} : { animation: "glow-breathe 4s ease-in-out infinite" }),
                  }}
                />
                <div className="absolute inset-0 rounded-full border-2 border-primary/30" />

                {/* Dashed SVG ring */}
                {!prefersReduced && (
                  <svg
                    className="absolute inset-[-8px] w-[calc(100%+16px)] h-[calc(100%+16px)] pointer-events-none z-[5]"
                    viewBox="0 0 240 240"
                    style={{ animation: "spin 30s linear infinite" }}
                  >
                    <circle
                      cx="120"
                      cy="120"
                      r="116"
                      fill="none"
                      stroke="hsl(166 84% 46%)"
                      strokeWidth="1"
                      strokeDasharray="4 4"
                      strokeOpacity="0.4"
                    />
                  </svg>
                )}

                <picture>
                  <source srcSet="/images/chris.webp" type="image/webp" />
                  <img
                    src="/images/chris.jpg"
                    alt="Chris Scibiorek"
                    width={615}
                    height={800}
                    className="w-full h-full rounded-full object-cover object-top relative z-10"
                    loading="lazy"
                    decoding="async"
                  />
                </picture>
              </div>
              <div className="mt-4 text-center lg:text-right">
                <p className="font-body font-semibold text-foreground text-sm">{a.whyMe.photoName}</p>
                <p className="text-xs font-mono text-muted-foreground tracking-wide">{a.whyMe.photoRole}</p>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-body font-medium text-muted-foreground hover:text-primary transition-colors mt-2"
                >
                  <Linkedin size={14} />
                  {a.whyMe.linkedinVerifyLabel}
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── S9: Is this a good fit? ───────────────────────────────────────────── */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="mb-12 text-center"
          >
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight">
              {a.fit.heading}
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Good fit */}
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.1, ...anim.transition }}
              className="relative rounded-xl border border-primary/30 bg-primary/5 p-8 overflow-hidden [@media(pointer:fine)]:hover:-translate-y-0.5 transition-[border-color,transform,box-shadow] duration-200"
              {...(!prefersReduced
                ? {
                    animate: {
                      boxShadow: [
                        "0 0 60px hsl(166 84% 46% / 0)",
                        "0 0 60px hsl(166 84% 46% / 0.2)",
                        "0 0 60px hsl(166 84% 46% / 0)",
                      ],
                    },
                    transition: {
                      delay: 0.1,
                      ...anim.transition,
                      boxShadow: { duration: 2.5, ease: "easeInOut", repeat: Infinity },
                    },
                  }
                : {})}
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />
              <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-primary/40 via-primary/10 to-transparent" />
              <p className="text-xs font-mono font-semibold tracking-widest text-primary uppercase mb-6">
                {a.fit.goodFitLabel}
              </p>
              <ul className="space-y-4">
                {a.fit.goodFit.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={prefersReduced ? { opacity: 1 } : { opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + i * 0.05, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-start gap-3"
                  >
                    <Check
                      size={15}
                      className="text-primary mt-0.5 flex-shrink-0"
                    />
                    <span className="text-sm font-body text-foreground leading-relaxed">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Less useful */}
            <motion.div
              initial={anim.initial}
              whileInView={anim.whileInView}
              viewport={anim.viewport}
              transition={{ delay: 0.2, ...anim.transition }}
              className="relative rounded-xl border border-border bg-card/40 p-8 overflow-hidden hover:border-border/60 transition-all duration-200"
            >
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />
              <div className="absolute top-0 left-0 bottom-0 w-px bg-gradient-to-b from-muted-foreground/20 via-muted-foreground/5 to-transparent" />
              <p className="text-xs font-mono font-semibold tracking-widest text-muted-foreground uppercase mb-6">
                {a.fit.lessFitLabel}
              </p>
              <ul className="space-y-4">
                {a.fit.lessFit.map((item, i) => (
                  <motion.li
                    key={i}
                    initial={prefersReduced ? { opacity: 1 } : { opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.05, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-start gap-3"
                  >
                    <X
                      size={14}
                      className="text-muted-foreground mt-0.5 flex-shrink-0"
                    />
                    <span className="text-sm font-body text-soft-foreground leading-relaxed">
                      {item}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── S10: Book a session ───────────────────────────────────────────────── */}
      <section className="py-16 bg-card/40">
        <div className="container mx-auto px-6">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={anim.transition}
            className="mb-12 text-center max-w-xl mx-auto"
          >
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {a.book.eyebrow}
            </p>
            <h2 className="text-4xl sm:text-5xl font-display tracking-tight mb-6">
              {a.book.heading}
            </h2>

            {/* Format items as outlined pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {a.book.formatItems.map((item) => (
                <span
                  key={item}
                  className="border border-border rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground"
                >
                  ● {item}
                </span>
              ))}
            </div>

            <div className="flex flex-col items-center mb-2">
              <span className="text-6xl font-display text-gradient-premium tabular-nums">
                {a.book.price}
              </span>
              <div className="w-5 h-px bg-primary/40 mx-auto mt-2 mb-1" />
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                {a.book.priceNote}
              </span>
            </div>
            {a.book.payNote && (
              <p className="text-xs font-body text-muted-foreground mt-1">
                {a.book.payNote}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.15, ...anim.transition }}
            className="relative flex items-center justify-center gap-3 p-4 rounded-xl border border-primary/20 bg-primary/5 max-w-3xl mx-auto mb-8"
          >
            <span className="absolute top-2 left-4 font-display text-5xl text-primary/30 pointer-events-none select-none leading-none">"</span>
            <p className="text-sm font-body text-muted-foreground text-center pl-10">
              {a.book.discoveryNote}
            </p>
          </motion.div>

        </div>

        {/* Cal.com embed — frameless. Cal iframe renders directly against the section bg (bg-card/40). No outer bg-card, no border, no shadow. Only rounded-2xl overflow-hidden to clip iframe corners. */}
        <div id="book" className="mx-auto w-full max-w-[880px] px-4 sm:px-6 scroll-mt-20 lg:scroll-mt-[400px]">
          <motion.div
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.2, ...anim.transition }}
            className="rounded-2xl overflow-hidden"
          >
            <div
              id="cal-advisory"
              className="h-[930px] sm:h-auto"
            />
          </motion.div>
        </div>

        <div className="container mx-auto px-6">
          <motion.p
            initial={anim.initial}
            whileInView={anim.whileInView}
            viewport={anim.viewport}
            transition={{ delay: 0.3, ...anim.transition }}
            className="text-sm font-body text-muted-foreground text-center mt-2 max-w-3xl mx-auto"
          >
            {a.book.fallback}{" "}
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline py-2.5 min-h-[44px] inline-flex items-center"
            >
              {a.book.fallbackLink}
            </a>
          </motion.p>

          <div className="mt-10">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* FAQ removed per Q7 */}

      <FooterCTA variant="oneToOne" />
    </div>
  );
};

/* ── ExpectationCard (S7 number flash) ─────────────────────────────── */
function ExpectationCard({
  index,
  item,
  anim,
  prefersReduced,
}: {
  index: number;
  item: string;
  anim: any;
  prefersReduced: boolean | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    if (!inView || prefersReduced) return;
    setFlashing(true);
    const timer = setTimeout(() => setFlashing(false), 500);
    return () => clearTimeout(timer);
  }, [inView, prefersReduced]);

  return (
    <motion.div
      ref={ref}
      initial={anim.initial}
      whileInView={anim.whileInView}
      viewport={anim.viewport}
      transition={{
        delay: index * 0.06,
        ...anim.transition,
      }}
      className="relative rounded-xl border border-border bg-card p-8 card-hover group overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <span
        className={`text-5xl sm:text-6xl font-display block mb-4 leading-none transition-colors duration-500 ${
          flashing ? "text-primary/60" : "text-primary/25 group-hover:text-primary/35"
        }`}
      >
        {String(index + 1).padStart(2, "0")}
      </span>
      <p className="text-base font-body font-semibold text-foreground leading-snug">
        {item}
      </p>
    </motion.div>
  );
}

export default Advisory;
